const chapter = {
  id: 'excretory-products',
  slug: 'excretory-products',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Excretory Products and their Elimination',
        hindi: 'उत्सर्जी उत्पाद और उनका निष्कासन',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'modes',
      title: {
        english: 'Modes of Excretion',
        hindi: 'उत्सर्जन के प्रकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'N-wastes formed from amino acid catabolism. Mode depends on water availability:',
        hindi: 'एन-अपशिष्ट अमीनो एसिड अपचय से बनता है। मोड पानी की उपलब्धता पर निर्भर करता है:',
            },
            {
              english: '**Ammonotelism** — excrete NH₃; needs lot of water; toxic. Bony fishes, aquatic amphibians, aquatic insects.',
        hindi: '**अमोनोटेलिज्म** - NH₃ का उत्सर्जन; पानी की बहुत जरूरत है; विषाक्त। बोनी मछलियाँ, जलीय उभयचर, जलीय कीड़े।',
            },
            {
              english: '**Ureotelism** — excrete urea; less toxic. Mammals, terrestrial amphibians, marine fishes (cartilaginous), humans. NH₃ + CO₂ → urea via **ornithine cycle** in liver.',
        hindi: '**यूरियोटेलिज्म** - यूरिया का उत्सर्जन; कम विषैला. स्तनधारी, स्थलीय उभयचर, समुद्री मछलियाँ (कार्टिलाजिनस), मनुष्य। NH₃ + CO₂ → लीवर में **ऑर्निथिन चक्र** के माध्यम से यूरिया।',
            },
            {
              english: '**Uricotelism** — excrete uric acid (semi-solid paste); least toxic, least water loss. Reptiles, birds, insects, land snails.',
        hindi: '**यूरिकोटेलिज्म** - यूरिक एसिड (अर्ध-ठोस पेस्ट) उत्सर्जित करें; कम से कम विषैला, कम से कम पानी का नुकसान। सरीसृप, पक्षी, कीड़े, भूमि घोंघे।',
            },
            {
              english: 'Excretory organs across phyla: protonephridia / flame cells (Platyhelminthes), nephridia (Annelida), Malpighian tubules (insects), antennal/green glands (crustaceans), kidneys (vertebrates).',
        hindi: 'फाइला में उत्सर्जन अंग: प्रोटोनफ्रिडिया / फ्लेम कोशिकाएं (प्लैटिहेल्मिन्थेस), नेफ्रिडिया (एनेलिडा), माल्पीघियन नलिकाएं (कीड़े), antennal/green ग्रंथियां (क्रस्टेशियंस), गुर्दे (कशेरुकी)।',
            },
          ],
        },
      ],
    },
    {
      id: 'human-system',
      title: {
        english: 'Human Excretory System',
        hindi: 'मानव उत्सर्जन तंत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Two **kidneys** (bean-shaped, retroperitoneal at T12–L3; ~10 × 5.5 × 3 cm, ~150 g each); ureters; urinary bladder; urethra.',
        hindi: 'दो **गुर्दे** (बीन के आकार के, टी12-एल3 पर रेट्रोपेरिटोनियल; ~10 × 5.5 × 3 सेमी, ~150 ग्राम प्रत्येक); मूत्रवाहिनी; मूत्राशय; मूत्रमार्ग.',
            },
            {
              english: 'Outer **cortex** + inner **medulla** (with renal pyramids → renal papillae → minor/major calyces → renal pelvis).',
        hindi: 'बाहरी **कॉर्टेक्स** + आंतरिक **मेडुला** (वृक्क पिरामिड के साथ → वृक्क पैपिला → minor/major कैलीस → वृक्क श्रोणि)।',
            },
            {
              english: '**Nephron** = structural & functional unit (~1 million per kidney). Two parts:',
        hindi: '**नेफ्रॉन** = संरचनात्मक और कार्यात्मक इकाई (~1 मिलियन प्रति किडनी)। दो भाग:',
            },
            {
              english: '• **Malpighian / renal corpuscle** = **glomerulus** (capillary tuft from afferent arteriole) inside **Bowman\'s capsule**.',
        hindi: '• **मैल्पीघियन/रीनल कॉर्पसकल** = **ग्लोमेरुलस** (अभिवाही धमनी से केशिका गुच्छ) **बोमन कैप्सूल** के अंदर।',
            },
            {
              english: '• **Renal tubule**: PCT → **Loop of Henle** (descending + ascending) → DCT → collecting duct.',
        hindi: '• **वृक्क नलिका**: पीसीटी → **हेनले का लूप** (अवरोही + आरोही) → डीसीटी → संग्रहण वाहिनी।',
            },
            {
              english: '**Cortical nephrons (~85 %)** — short loop barely into medulla.',
        hindi: '**कॉर्टिकल नेफ्रॉन (~85%)** - मज्जा में बमुश्किल छोटा लूप।',
            },
            {
              english: '**Juxtamedullary nephrons (~15 %)** — long loop deep into medulla; have **vasa recta** (long parallel capillary). Essential for concentrating urine.',
        hindi: '**जक्सटामेडुलरी नेफ्रॉन (~15%)** - मज्जा में गहरा लंबा लूप; **वासा रेक्टा** (लंबी समानांतर केशिका) है। मूत्र को एकाग्र करने के लिए आवश्यक।',
            },
            {
              english: '**Blood supply**: renal artery → afferent arteriole → glomerulus → efferent arteriole → peritubular capillaries / vasa recta → renal vein. Note: **two capillary beds in series**.',
        hindi: '**रक्त आपूर्ति**: वृक्क धमनी → अभिवाही धमनी → ग्लोमेरुलस → अपवाही धमनी → पेरिटुबुलर केशिकाएं / वासा रेक्टा → वृक्क शिरा। नोट: **श्रृंखला में दो केशिका बिस्तर**।',
            },
          ],
        },
      ],
    },
    {
      id: 'urine-formation',
      title: {
        english: 'Urine Formation',
        hindi: 'मूत्र निर्माण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Three steps: **Glomerular filtration → Tubular reabsorption → Tubular secretion**.',
        hindi: 'तीन चरण: **ग्लोमेरुलर निस्पंदन → ट्यूबलर पुनर्अवशोषण → ट्यूबलर स्राव**।',
            },
            {
              english: '**1. Glomerular filtration** — at Bowman\'s capsule. Hydrostatic blood pressure (~50 mmHg) opposed by capsular pressure (~15) and colloidal osmotic pressure (~30). **Net filtration pressure ≈ 5-10 mmHg**.',
        hindi: '**1. ग्लोमेरुलर निस्पंदन** - बोमन कैप्सूल पर। हाइड्रोस्टेटिक रक्तचाप (~50 mmHg) कैप्सुलर दबाव (~15) और कोलाइडल आसमाटिक दबाव (~30) द्वारा विरोध किया जाता है। **शुद्ध निस्पंदन दबाव ≈ 5-10 mmHg**।',
            },
            {
              english: '• Filter = endothelium + basement membrane + podocytes (slit pores).',
        hindi: '• फिल्टर = एंडोथेलियम + बेसमेंट मेम्ब्रेन + पोडोसाइट्स (स्लिट पोर्स)।',
            },
            {
              english: '• **GFR** (Glomerular Filtration Rate) ≈ **125 mL/min ≈ 180 L/day**.',
        hindi: '• **GFR** (ग्लोमेरुलर निस्पंदन दर) ≈ **125 mL/min ≈ 180 L/day**।',
            },
            {
              english: '• Regulated by **JGA (Juxtaglomerular Apparatus)** — cellular modification at contact of afferent arteriole and DCT. ↓ GFR → renin release → angiotensin II → aldosterone → ↑ Na⁺ & water reabsorption → ↑ BP.',
        hindi: '• **JGA (Juxtaglomerular Apparatus)** द्वारा विनियमित - अभिवाही धमनी और DCT के संपर्क पर सेलुलर संशोधन। ↓ जीएफआर → रेनिन रिलीज → एंजियोटेंसिन II → एल्डोस्टेरोन → ↑ Na⁺ और जल पुनर्अवशोषण → ↑ बीपी।',
            },
            {
              english: '**2. Tubular reabsorption** — ~99 % filtrate reabsorbed (only ~1.5 L urine/day). Sites:',
        hindi: '**2. ट्यूबलर पुनर्अवशोषण** - ~99% निस्पंद पुनःअवशोषित (केवल ~1.5 एल urine/day)। साइटें: • **पीसीटी (अधिकांश, ~70%)** - Na⁺, K⁺, ग्लूकोज (100%), अमीनो एसिड (100%), HCO₃⁻ (सक्रिय); परासरण द्वारा जल.',
            },
            {
              english: '• **PCT (most, ~70 %)** — Na⁺, K⁺, glucose (100 %), amino acids (100 %), HCO₃⁻ (active); water by osmosis.',
        hindi: '• **पीसीटी (अधिकांश, ~70 %)** — Na⁺, K⁺, ग्लूकोज (100 %), अमीनो एसिड (100 %), HCO₃⁻ (सक्रिय); परासरण द्वारा जल.',
            },
            {
              english: '• **Loop of Henle** — descending limb permeable to water, impermeable to salt; ascending limb opposite. Creates **counter-current multiplier** in medulla.',
        hindi: '• **हेनले का लूप** - पानी के लिए पारगम्य, पानी के लिए अभेद्य, उतरता हुआ अंग; आरोही अंग विपरीत. मज्जा में **काउंटर-करंट मल्टीप्लायर** बनाता है।',
            },
            {
              english: '• **DCT** — Na⁺, water (under aldosterone, ADH); H⁺, K⁺ secretion.',
        hindi: '• **DCT** - Na⁺, पानी (एल्डोस्टेरोन, ADH के तहत); H⁺, K⁺ स्राव.',
            },
            {
              english: '• **Collecting duct** — final concentration; permeability to water controlled by **ADH** (Vasopressin from posterior pituitary).',
        hindi: '• **संग्रहण वाहिनी** — अंतिम एकाग्रता; पानी में पारगम्यता **एडीएच** (पश्च पिट्यूटरी से वैसोप्रेसिन) द्वारा नियंत्रित होती है।',
            },
            {
              english: '**3. Tubular secretion** — H⁺, K⁺, NH₃, organic acids/bases secreted into lumen. Maintains pH.',
        hindi: '**3. ट्यूबलर स्राव** - H⁺, K⁺, NH₃, कार्बनिक acids/bases लुमेन में स्रावित होता है। पीएच बनाए रखता है.',
            },
          ],
        },
      ],
    },
    {
      id: 'concentration-regulation',
      title: {
        english: 'Counter-Current & Hormonal Regulation',
        hindi: 'प्रति-धारा और हार्मोनल नियमन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Counter-current mechanism** — Loop of Henle + vasa recta produce **cortico-medullary osmotic gradient** (~300 mOsm/L cortex → ~1200 mOsm/L deep medulla). Maintained by NaCl + urea recycling.',
        hindi: '**काउंटर-करंट मैकेनिज्म** - हेनले का लूप + वासा रेक्टा **कॉर्टिको-मेडुलरी ऑस्मोटिक ग्रेडिएंट** (~300 mOsm/L कॉर्टेक्स → ~1200 mOsm/L डीप मेडुला) उत्पन्न करता है। NaCl + यूरिया रीसाइक्लिंग द्वारा बनाए रखा गया।',
            },
            {
              english: 'Allows kidney to produce hyperosmotic urine (~4× plasma).',
        hindi: 'किडनी को हाइपरऑस्मोटिक मूत्र (~4× प्लाज्मा) उत्पन्न करने की अनुमति देता है।',
            },
            {
              english: '**ADH (Vasopressin)** — ↑ water reabsorption in DCT/CD → concentrated urine. Released when blood is hyperosmotic. Deficiency → **diabetes insipidus** (excess dilute urine).',
        hindi: '**एडीएच (वैसोप्रेसिन)** — ↑ DCT/CD → सांद्रित मूत्र में पानी का पुनर्अवशोषण। रक्त हाइपरऑस्मोटिक होने पर जारी किया जाता है। कमी → **डायबिटीज इन्सिपिडस** (अत्यधिक पतला मूत्र)।',
            },
            {
              english: '**RAAS** — drop in BP/Na⁺ → JGA cells release renin → angiotensinogen → angiotensin I → angiotensin II (vasoconstrictor) → adrenal cortex releases **aldosterone** → ↑ Na⁺ & H₂O reabsorption → BP restored.',
        hindi: '**RAAS** - BP/Na⁺ में गिरावट → JGA कोशिकाएं रेनिन छोड़ती हैं → एंजियोटेंसिनोजेन → एंजियोटेंसिन I → एंजियोटेंसिन II (वासोकोनस्ट्रिक्टर) → एड्रेनल कॉर्टेक्स रिलीज **एल्डोस्टेरोन** → ↑ Na⁺ और H₂O पुनर्अवशोषण → BP बहाल.',
            },
            {
              english: '**ANF (Atrial Natriuretic Factor)** — from atrial walls when BP rises; vasodilator → ↓ BP. Antagonist to RAAS.',
        hindi: '**एएनएफ (एट्रियल नैट्रियूरेटिक फैक्टर)** - बीपी बढ़ने पर अलिंद की दीवारों से; वैसोडिलेटर → ↓ बीपी। रास का विरोधी।',
            },
          ],
        },
      ],
    },
    {
      id: 'micturition-disorders',
      title: {
        english: 'Micturition & Disorders',
        hindi: 'मूत्रण और विकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Micturition** = release of urine. Stretch receptors in bladder → CNS reflex → relaxation of sphincters; bladder contracts.',
        hindi: '**मूत्रस्राव** = मूत्र का निकलना। मूत्राशय में खिंचाव रिसेप्टर्स → सीएनएस रिफ्लेक्स → स्फिंक्टर्स की छूट; मूत्राशय सिकुड़ जाता है.',
            },
            {
              english: 'Composition of urine: ~95 % water; urea (~2 %), Na⁺, K⁺, creatinine, uric acid; light yellow due to **urochrome**. ~1-1.8 L/day. Slightly acidic (pH ~6.0).',
        hindi: 'मूत्र की संरचना: ~95% पानी; यूरिया (~2%), Na⁺, K⁺, क्रिएटिनिन, यूरिक एसिड; **यूरोक्रोम** के कारण हल्का पीला। ~1-1.8 L/day. थोड़ा अम्लीय (पीएच ~6.0)।',
            },
            {
              english: '**Disorders**:',
        hindi: '**विकार**:',
            },
            {
              english: '• **Uraemia** — urea accumulation in blood; in renal failure; treated by **haemodialysis** (artificial kidney; cellophane membrane; heparin used).',
        hindi: '• **यूरेमिया** - रक्त में यूरिया का संचय; गुर्दे की विफलता में; **हेमोडायलिसिस** द्वारा उपचार किया जाता है (कृत्रिम किडनी; सिलोफ़न झिल्ली; हेपरिन का उपयोग किया जाता है)।',
            },
            {
              english: '• **Renal calculi (stones)** — crystallised oxalates etc.',
        hindi: '• **गुर्दे की पथरी (पत्थर)** - क्रिस्टलीकृत ऑक्सालेट आदि।',
            },
            {
              english: '• **Glomerulonephritis** — inflammation of glomeruli.',
        hindi: '• **ग्लोमेरुलोनेफ्राइटिस** - ग्लोमेरुली की सूजन।',
            },
            {
              english: '• **Renal failure** — kidney transplant only permanent treatment.',
        hindi: '• **गुर्दे की विफलता** — गुर्दा प्रत्यारोपण केवल स्थायी उपचार है।',
            },
            {
              english: 'Other excretory roles: **lungs** (CO₂, water), **liver** (bile pigments, cholesterol, drugs), **skin** (sweat, sebum).',
        hindi: 'अन्य उत्सर्जन भूमिकाएँ: **फेफड़े** (CO₂, पानी), **यकृत** (पित्त वर्णक, कोलेस्ट्रॉल, औषधियाँ), **त्वचा** (पसीना, सीबम)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{GFR} \\approx 125\\ \\text{mL/min} \\approx 180\\ \\text{L/day}',
      desc: {
        english: '',
        hindi: 'जीएफआर 125\\ एमएल/मिनट 180\\ एल/दिन',
      },
    },
    {
      latex: '\\text{Urine output} \\approx 1.5\\ \\text{L/day (only ~1\\% of filtrate)}',
      desc: {
        english: '',
        hindi: 'मूत्र उत्पादन 1.5\\ लीटर/दिन (केवल ~1\\% निस्पंद)',
      },
    },
    {
      latex: '\\text{Net filtration pressure} \\approx 5\\text{-}10\\ \\text{mmHg}',
      desc: {
        english: '',
        hindi: 'शुद्ध निस्पंदन दबाव 5-10\\ mmHg',
      },
    },
    {
      latex: 'NH_3 \\to \\text{Urea (ornithine cycle, liver)}',
      desc: {
        english: '',
        hindi: 'एनएच 3 यूरिया (ऑर्निथिन चक्र, यकृत)',
      },
    },
    {
      latex: 'PCT \\text{ reabsorbs } \\sim 70\\% \\text{ of filtrate}',
      desc: {
        english: '',
        hindi: 'पीसीटी 70\\% निस्पंद को पुनः अवशोषित कर लेता है',
      },
    },
    {
      latex: '\\text{Cortico-medullary gradient: 300} \\to 1200\\ \\text{mOsm/L}',
      desc: {
        english: '',
        hindi: 'कॉर्टिको-मेडुलरी ग्रेडिएंट: 300 1200\\ mOsm/L',
      },
    },
    {
      latex: '\\text{RAAS: Renin} \\to \\text{Ang II} \\to \\text{Aldosterone} \\to \\uparrow Na^+ \\& BP',
      desc: {
        english: '',
        hindi: 'रास: रेनिन आंग II एल्डोस्टेरोन Na + \\& BP',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Nephron',
      problem: {
        english: 'Glucose is reabsorbed completely in:',
        hindi: 'ग्लूकोज पूरी तरह से पुन: अवशोषित हो जाता है:',
      },
      answer: {
        english: 'Proximal Convoluted Tubule (PCT)',
        hindi: 'समीपस्थ कुंडलित नलिका (पीसीटी)',
      },
      solution: {
        english: '100 % glucose & amino acids reabsorbed in PCT.',
        hindi: 'पीसीटी में 100% ग्लूकोज और अमीनो एसिड पुनः अवशोषित।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hormone',
      problem: {
        english: 'Deficiency of ADH causes:',
        hindi: 'ADH की कमी के कारण:',
      },
      answer: {
        english: 'Diabetes insipidus (excessive dilute urine)',
        hindi: 'डायबिटीज इन्सिपिडस (अत्यधिक पतला मूत्र)',
      },
      solution: {
        english: 'ADH controls water reabsorption in collecting duct.',
        hindi: 'ADH संग्रहण वाहिनी में जल पुनर्अवशोषण को नियंत्रित करता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Excretion',
      problem: {
        english: 'Animals excreting uric acid are called:',
        hindi: 'यूरिक एसिड उत्सर्जित करने वाले जंतु कहलाते हैं:',
      },
      answer: {
        english: 'Uricotelic (reptiles, birds, insects)',
        hindi: 'यूरिकोटेलिक (सरीसृप, पक्षी, कीड़े)',
      },
      solution: {
        english: 'Adaptation to water conservation.',
        hindi: 'जल संरक्षण के लिए अनुकूलन।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'JGA',
      problem: {
        english: 'Renin is secreted by:',
        hindi: 'रेनिन का स्राव होता है:',
      },
      answer: {
        english: 'Juxtaglomerular cells of afferent arteriole',
        hindi: 'अभिवाही धमनी की जक्सटाग्लोमेरुलर कोशिकाएं',
      },
      solution: {
        english: 'Triggered by ↓ BP / ↓ Na⁺ in DCT.',
        hindi: 'यह DCT में Na⁺ की कमी या रक्तचाप में गिरावट से उद्दीप्त होता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Counter current',
      problem: {
        english: 'Counter-current mechanism is associated with:',
        hindi: 'काउंटर-करंट तंत्र संबंधित है:',
      },
      answer: {
        english: 'Loop of Henle and vasa recta',
        hindi: 'हेनले और वासा रेक्टा का लूप',
      },
      solution: {
        english: 'Maintains medullary osmotic gradient.',
        hindi: 'मेडुलरी ऑस्मोटिक ग्रेडिएंट को बनाए रखता है।',
      },
    },
  ],
  next: {
    slug: 'locomotion-movement',
    title: 'Locomotion and Movement',
  },
};

export default chapter;
